import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Checkbox, CircularProgress, Fade, FormControlLabel, IconButton, Stack, Tooltip, Chip, Badge, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import ErrorBoundary from '../ErrorBoundary';
import { motion } from 'framer-motion';
import API, { RBAC } from '../../services/api';
import GeneralModal from '../GeneralModal';
import { DataGrid } from '@mui/x-data-grid';
import { Abc, Add, ArrowBackIosNew, ArrowForwardIos, Check, Discount, EditNote, FormatListBulleted, InsertDriveFile, Numbers, SkipNext, Task, Image, Visibility, Delete, CheckCircleRounded, UploadFile, InfoOutlined } from '@mui/icons-material';
import DatePickV2 from '../DatePickV2';
import CustomAutoComplete from '../CustomAutoComplete';
import CustomSwitch from '../CustomSwitch';
import PickProduct from '../../pages/Order/PickProduct';
import PickPromotion from '../../pages/Order/PickPromotion';
import TextBox from '../TextBox';
import EditOrder from '../../pages/Order/EditOrder';
import { useState, useCallback, useRef } from 'react';
import AddInvoiceDetail from '../../pages/Invoice/AddInvoiceDetail';
import RefreshButtonSpinner from '../RefreshButtonSpinner';
import VoucherButton from '../VoucherButton';
import CustomTableInvoice from '../CustomTableInvoice';
import { useEffect } from 'react';
import CollapsedProductsTable from '../CollapsedProductsTable';
import ButtonNotes from '../ButtonNotes';
import SuccessMessage from '../SuccessMessage';
import { useAuth } from '../../auth/AuthProvider';
import { hasAny } from '../../auth/permissions';
import { useMemo } from 'react';
import CustomModal from '../CustomModal';
import CustomSnackBar from '../CustomSnackBar';
import AdjustmentBox from '../AdjustmentBox/index.jsx';
import {
  buildManualInvoiceDetailLines,
  buildReconciledInvoicePayload,
  normalizeInvoiceLineFormat,
} from './invoiceReconciliation';
import { normalizePaymentStatus, PAYMENT_STATUS } from '../../utils/paymentStatus';

const DRAFT_KEY = 'invoice:draft';
// Visualizador de documento (imagen/PDF)
const DocumentPreview = ({ src, type, setOpen, onDelete }) => (
  <Box sx={{ p: 2 }}>
    {type === 'pdf' ? (
      <Box sx={{ width: '100%', height: '70vh' }}>
        <iframe src={src} title="Documento" style={{ width: '100%', height: '100%', border: 0 }} />
      </Box>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <img src={src} alt="Documento" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
      </Box>
    )}
    <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mt: 2 }}>
      <Button variant='outlined' onClick={() => setOpen(false)}>Cerrar</Button>
      {onDelete && (
        <Button variant='contained' color='error' startIcon={<Delete />} onClick={() => { onDelete(); setOpen(false); }}>
          Eliminar
        </Button>
      )}
    </Stack>
  </Box>
);
const toISODate = (val) => {
  if (!val) return null;

  if (typeof val === 'string') {
    // Ya viene correcto
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    // DD/MM/YYYY -> YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
      const [dd, mm, yyyy] = val.split('/');
      return `${yyyy}-${mm}-${dd}`;
    }
    if (val.includes('T')) return val.split('T')[0];
  }

  if (val instanceof Date && !isNaN(val)) {
    return val.toISOString().split('T')[0];
  }

  const d = new Date(val);
  return isNaN(d) ? null : d.toISOString().split('T')[0];
};

const resolveLegacyWarehouseText = (note, logicalWarehouses = []) => {
  const direct = note?.legacy_warehouse_text || note?.warehouse;
  if (direct !== undefined && direct !== null) {
    const cleaned = String(direct).trim();
    if (cleaned) return cleaned;
  }
  const logicalId = note?.logical_warehouse_id ?? note?.logical_warehouse?.id ?? null;
  if (logicalId == null) return null;
  const match = (logicalWarehouses || []).find((l) => String(l.id) === String(logicalId));
  const fallback =
    match?.code ||
    match?.name ||
    note?.logical_warehouse?.code ||
    note?.logical_warehouse?.name ||
    null;
  if (!fallback) {
    const fallbackId = String(logicalId || '').trim();
    return fallbackId || null;
  }
  const cleaned = String(fallback).trim();
  return cleaned || null;
};

const buildNotePayload = (note, logicalWarehouses = []) => {
  const payload = {
    id: note?.id,
    number: note?.number,
    comment: note?.comment,
  };
  if (note?.date) {
    payload.date = toISODate(note.date);
  }
  const logicalId = note?.logical_warehouse_id ?? note?.logical_warehouse?.id ?? null;
  if (logicalId) payload.logical_warehouse_id = logicalId;
  const legacyText = resolveLegacyWarehouseText(note, logicalWarehouses);
  if (legacyText) {
    payload.legacy_warehouse_text = legacyText;
    if (!logicalId) {
      payload.warehouse = legacyText;
    }
  }
  return payload;
};

const DRAFT_INVOICE_NOTE_LINK_ID = '__draft_invoice__';

const normalizeNoteLinks = (note) => {
  const sources = [
    note?.linkedInvoices,
    note?.linked_invoices,
    note?.invoiceIds,
    note?.invoice_ids,
    note?.invoices,
  ];

  return sources.flatMap((source) => {
    if (!source) return [];
    const list = Array.isArray(source) ? source : [source];
    return list.map((item) => {
      if (item && typeof item === 'object') {
        return item.id ?? item.invoice_id ?? item.invoiceId ?? null;
      }
      return item;
    });
  }).map((value) => String(value ?? '').trim()).filter(Boolean);
};

const isValidNote = (note) => {
  if (!note || typeof note !== 'object') return false;
  if (note.is_deleted === true || note.deleted === true || note.is_active === false) return false;
  return [note.id, note.number, note.comment, note.date].some((value) => String(value ?? '').trim());
};

const getInvoiceNoteTargetIds = (invoice, draftLinkId = DRAFT_INVOICE_NOTE_LINK_ID) => new Set([
  draftLinkId,
  invoice?.id,
  invoice?.invoice_id,
  invoice?.invoiceId,
].map((value) => String(value ?? '').trim()).filter(Boolean));

const isValidLinkedNote = (note, targetIds) => {
  if (!isValidNote(note)) return false;
  const links = normalizeNoteLinks(note);
  if (links.length === 0) return false;
  if (!targetIds || targetIds.size === 0) return true;
  return links.some((link) => targetIds.has(link));
};

const hasLinkedNotes = (invoice, notes = [], draftLinkId = DRAFT_INVOICE_NOTE_LINK_ID) => {
  const targetIds = getInvoiceNoteTargetIds(invoice, draftLinkId);
  return (Array.isArray(notes) ? notes : []).some((note) => isValidLinkedNote(note, targetIds));
};

const getLinkedNotesForInvoice = (invoice, notes = [], draftLinkId = DRAFT_INVOICE_NOTE_LINK_ID) => {
  const targetIds = getInvoiceNoteTargetIds(invoice, draftLinkId);
  return (Array.isArray(notes) ? notes : []).filter((note) => isValidLinkedNote(note, targetIds));
};

const normalizeTypeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

const isFacturaCambiariaName = (value) => {
  const normalized = normalizeTypeName(value);
  return normalized.includes('cambiaria') && (normalized.includes('factura') || normalized.includes('fctura'));
};

const isFacturaElectronicaName = (value) => {
  const normalized = normalizeTypeName(value);
  return normalized.includes('electronica') && (normalized.includes('factura') || normalized.includes('fctura'));
};

const InvoiceTypeSelect = ({ types, hasInvoiceTypes, safeInvoiceTypeId, onSelect }) => {
  const placeholder = 'Sin tipos de factura';
  const showLabel = hasInvoiceTypes;
  const safeValue = typeof safeInvoiceTypeId === 'object'
    ? (safeInvoiceTypeId?.id ?? '')
    : (safeInvoiceTypeId ?? '');
  const renderValue = (selected) => {
    if (!hasInvoiceTypes) {
      return (
        <Box sx={{ width: '100%', textAlign: 'center', color: 'text.secondary' }}>
          {placeholder}
        </Box>
      );
    }
    if (!selected) return '';
    if (typeof selected === 'object') return selected?.name || '';
    const found = (types || []).find(t => String(t.id) === String(selected));
    return found?.name || '';
  };

  return (
    <FormControl size='small' sx={{ minWidth: 180 }}>
      {showLabel && <InputLabel>Tipo de factura</InputLabel>}
      <Select
        label={showLabel ? 'Tipo de factura' : undefined}
        value={hasInvoiceTypes ? safeValue : ''}
        onChange={(e) => {
          const id = e.target.value;
          const selected = (types || []).find(t => String(t.id) === String(id)) || null;
          onSelect(selected);
        }}
        disabled={!hasInvoiceTypes}
        renderValue={renderValue}
        displayEmpty
      >
        {(types || []).map(t => (
          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
        ))}
      </Select>
      {!hasInvoiceTypes && (
        <FormHelperText sx={{ color: 'text.secondary' }}>
          Crea un tipo de factura para continuar
        </FormHelperText>
      )}
    </FormControl>
  );
};

function CustomStepperInvoice({ invoice,
  setInvoice, setValues, handleSupplierChange, types, values,
  handleCheckboxChange, orders, columns, open, setOpen, selectedOrder, updateOrderList, setSelectedOrder,
  selectionModel,
  onSelectionModelChange, columnsSummary, renderExtras, userData,
  flow, setFlowField, resetFlow,
  initialSearchTerm, onSearchTermChange }) {
  const [activeStepLocal, setActiveStepLocal] = useState(0);
  const [open2, setOpen2] = useState(false);
  const [completedLocal, setCompletedLocal] = useState({});
  const [loading, setLoading] = useState(false);
  const [productsInvoice, setProductsInvoice] = useState([])
  const [logicalWarehouses, setLogicalWarehouses] = useState([]);
  const { permissions, isSuperuser, company } = useAuth();
  const canUseLogical = isSuperuser || hasAny(permissions, ['view_catalogs', 'manage_catalogs']);
  const hasInvoiceTypes = Array.isArray(types) && types.length > 0;
  const defaultInvoiceType = hasInvoiceTypes ? types[0] : null;
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [detailUnified, setDetailUnified] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCacheKey, setDetailCacheKey] = useState(null);
  const detailModalKey = useMemo(() => {
    const unified = detailUnified;
    const directId = unified?.id ?? unified?.order_id ?? unified?.orderId ?? null;
    if (directId != null) return String(directId);
    const topSources = unified?.source_orders || unified?.sourceOrders || [];
    if (topSources.length) return JSON.stringify(topSources);
    const detailSources = (unified?.order_details || []).flatMap(
      (detail) => detail?.source_orders || detail?.sourceOrders || []
    );
    if (detailSources.length) return JSON.stringify(detailSources);
    return detailCacheKey || 'detail';
  }, [detailUnified, detailCacheKey]);
  const manualOrderSelectionEnabled = false;
  const steps = React.useMemo(
    () => ['Datos de factura', 'Agregar productos y bonificaciones', 'Confirmar ingreso', 'Finalizar ingreso'],
    []
  );

  React.useEffect(() => {
    let mounted = true;
    if (!canUseLogical) {
      setLogicalWarehouses([]);
      return () => { mounted = false; };
    }
    (async () => {
      try {
        const params = company?.id ? { company: company.id } : undefined;
        const res = await RBAC.logicalWarehouses.list(params);
        if (mounted) setLogicalWarehouses(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setLogicalWarehouses([]);
      }
    })();
    return () => { mounted = false; };
  }, [canUseLogical, company?.id]);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [supplierAllProducts, setSupplierAllProducts] = useState(false);
  const [openPromotionModal, setOpenPromotionModal] = useState(false);
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  const [currentInvoiceDetailIndex, setCurrentDetailIndex] = useState(null);
  const [arrivedViaSkip, setArrivedViaSkip] = useState(false);
  const invoiceProductIdSet = React.useMemo(() => (
    new Set(
      (invoice?.invoice_detail || [])
        .map((detail) => Number(detail?.product_id || detail?.product?.id))
        .filter((id) => Number.isFinite(id))
    )
  ), [invoice?.invoice_detail]);
  const noteTargetInvoices = React.useMemo(() => ([{
    id: DRAFT_INVOICE_NOTE_LINK_ID,
    number: invoice?.number || 'actual',
    series: invoice?.series || '',
  }]), [invoice?.number, invoice?.series]);
  const invoiceHasLinkedNotes = React.useMemo(
    () => hasLinkedNotes(invoice, notes, DRAFT_INVOICE_NOTE_LINK_ID),
    [invoice, notes]
  );
  const showSuccessMessage = invoiceHasLinkedNotes && !arrivedViaSkip;
  const showNoNotesAlert = !arrivedViaSkip && !invoiceHasLinkedNotes;

  const linkNotesToInvoice = React.useCallback((invoiceId) => {
    if (!invoiceId) return;
    setNotes(prev => (prev || []).map(note => {
      const current = Array.isArray(note?.linkedInvoices) ? note.linkedInvoices : [];
      if (current.includes(invoiceId)) return note;
      return { ...note, linkedInvoices: [...current, invoiceId] };
    }));
  }, []);
  const findTypeByCodeOrName = React.useCallback((code, nameFallback) => {
    const upper = String(code || '').toUpperCase();
    const byCode = (types || []).find(t => String(t?.code || '').toUpperCase() === upper);
    if (byCode) return byCode;
    const normalizedName = normalizeTypeName(nameFallback);
    return (types || []).find(t => {
      const typeName = normalizeTypeName(t?.name);
      if (typeName === normalizedName) return true;
      if (upper === 'FC' && isFacturaCambiariaName(typeName)) return true;
      if (upper === 'FE' && isFacturaElectronicaName(typeName)) return true;
      if (upper === 'V' && typeName.includes('vale')) return true;
      return false;
    }) || null;
  }, [types]);

  const facturaCambiariaType = React.useMemo(
    () => findTypeByCodeOrName('FC', 'Factura Cambiaria') || defaultInvoiceType,
    [findTypeByCodeOrName, defaultInvoiceType]
  );
  const facturaElectronicaType = React.useMemo(
    () => findTypeByCodeOrName('FE', 'Factura Electronica'),
    [findTypeByCodeOrName]
  );
  const valeType = React.useMemo(
    () => findTypeByCodeOrName('V', 'Vale'),
    [findTypeByCodeOrName]
  );

  const codeToType = React.useMemo(() => ({
    'FC': facturaCambiariaType,
    'FE': facturaElectronicaType || facturaCambiariaType,
    'V': valeType || facturaCambiariaType
  }), [facturaCambiariaType, facturaElectronicaType, valeType]);

  const typeToCode = React.useMemo(() => ({
    [normalizeTypeName('Factura Cambiaria')]: 'FC',
    [normalizeTypeName('Factura Electronica')]: 'FE',
    [normalizeTypeName('Vale')]: 'V'
  }), []);


  const activeStep = flow?.activeStep ?? activeStepLocal;
  const completed = flow?.completed ?? completedLocal;

  const setActiveStep = (val) => {
    const v = typeof val === 'function' ? val(activeStep) : val;
    if (setFlowField) setFlowField('activeStep', v);
    else setActiveStepLocal(v);
  };
  const setCompleted = (val) => {
    const v = typeof val === 'function' ? val(completed) : val;
    if (setFlowField) setFlowField('completed', v);
    else setCompletedLocal(v);
  };

  const resolveCodeFromType = React.useCallback((maybeType) => {
    if (!maybeType) return null;
    if (typeof maybeType === 'string') {
      const trimmed = maybeType.trim();
      const normalized = normalizeTypeName(trimmed);
      if (isFacturaCambiariaName(normalized)) return 'FC';
      if (isFacturaElectronicaName(normalized)) return 'FE';
      const codeFromName = typeToCode[normalized];
      if (codeFromName) return codeFromName;
      const upper = trimmed.toUpperCase();
      if (['FC', 'FE', 'V'].includes(upper)) return upper;
      return null;
    }
    if (typeof maybeType === 'number') {
      const found = (types || []).find(t => String(t.id) === String(maybeType));
      return found ? (found.code ? String(found.code).toUpperCase() : typeToCode[normalizeTypeName(found.name)] || null) : null;
    }
    if (maybeType.code) return String(maybeType.code).toUpperCase();
    if (maybeType.name) {
      const normalized = normalizeTypeName(maybeType.name);
      if (isFacturaCambiariaName(normalized)) return 'FC';
      if (isFacturaElectronicaName(normalized)) return 'FE';
      return typeToCode[normalized] || null;
    }
    return null;
  }, [typeToCode, types]);

  useEffect(() => {
    if (!facturaCambiariaType) return;
    setInvoice(prev => {
      if (!prev) return prev;
      const currentCode = resolveCodeFromType(prev.invoiceType)
        || resolveCodeFromType(prev.invoice_type)
        || resolveCodeFromType(prev.invoice_type_id);
      if (currentCode && !['FE', 'FC'].includes(currentCode)) return prev;
      if (currentCode === 'FC' && prev.invoice_type_id && prev.invoice_type) return prev;
      return {
        ...prev,
        invoiceType: 'FC',
        invoice_type: facturaCambiariaType || null,
        invoice_type_id: facturaCambiariaType?.id ?? null,
      };
    });
  }, [facturaCambiariaType, resolveCodeFromType, setInvoice]);

  useEffect(() => {
    setInvoice(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      let changed = false;

      const normalizedPaymentStatus = normalizePaymentStatus(prev.paymentStatus);
      if (normalizedPaymentStatus !== prev.paymentStatus) {
        next.paymentStatus = normalizedPaymentStatus;
        changed = true;
      }

      const normalizedNumber = typeof prev.number === 'object' ? '' : (prev.number ?? '');
      if (normalizedNumber !== prev.number) { next.number = normalizedNumber; changed = true; }

      const normalizedSeries = typeof prev.series === 'object' ? '' : (prev.series ?? '');
      if (normalizedSeries !== prev.series) { next.series = normalizedSeries; changed = true; }

      // Asegurar que invoice_type_id sea siempre primitivo (id)
      const normalizedTypeId = typeof prev.invoice_type_id === 'object'
        ? (prev.invoice_type_id?.id ?? null)
        : (prev.invoice_type_id ?? null);
      if (normalizedTypeId !== prev.invoice_type_id) { next.invoice_type_id = normalizedTypeId; changed = true; }

      const inferredCode = resolveCodeFromType(prev.invoiceType)
        || resolveCodeFromType(prev.invoice_type)
        || resolveCodeFromType(prev.invoice_type_id)
        || null;

      if (inferredCode && inferredCode !== prev.invoiceType) {
        next.invoiceType = inferredCode;
        changed = true;
      }

      if (!prev.invoice_type_id && inferredCode) {
        const found = (types || []).find(t => {
          const code = t?.code ? String(t.code).toUpperCase() : typeToCode[normalizeTypeName(t?.name)];
          return code === inferredCode;
        });
        if (found?.id) {
          next.invoice_type_id = found.id;
          next.invoice_type = found;
          changed = true;
        }
      }

      // Si tenemos id pero no objeto asignado, hidratar invoice_type para Select/display
      if (!next.invoice_type && normalizedTypeId) {
        const found = (types || []).find(t => String(t.id) === String(normalizedTypeId));
        if (found) {
          next.invoice_type = found;
          changed = true;
        } else if (hasInvoiceTypes) {
          next.invoice_type_id = null;
          next.invoice_type = null;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [hasInvoiceTypes, resolveCodeFromType, setInvoice, typeToCode, types]);


  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const isPenultimateStep = () => activeStep === totalSteps() - 2;

  const handleChange = useCallback((name, value) => {
    setInvoice(prev => {
      const cleanValue = typeof value === 'object'
        ? (name === 'number' || name === 'series' ? value.value || '' : value.id || '')
        : value;

      return { ...prev, [name]: cleanValue };
    });
  }, []);

  const handleAdjustmentChange = useCallback((name, value) => {
    const raw = String(value ?? '');
    if (raw !== '' && !/^-?\d*(\.\d{0,2})?$/.test(raw)) {
      return;
    }
    setInvoice((prev) => ({ ...prev, [name]: raw }));
  }, [setInvoice]);
  const handleSelectChange = (name, value) => {
    const resolvedType = resolveCodeFromType(value) || 'FC';
    const resolvedObj = (() => {
      if (value && typeof value === 'object' && 'id' in value) return value;
      if (value && typeof value !== 'object') {
        return (types || []).find(t => String(t.id) === String(value)) || null;
      }
      return null;
    })();

    setInvoice(prevState => ({
      ...prevState,
      // legacy code for backend compatibility
      [name]: resolvedType,
      // keep object + id for UI and posting
      invoice_type: resolvedObj || null,
      invoice_type_id: resolvedObj?.id || null,
      ...(resolvedType === 'V' && {
        number: '',
        series: '' // Limpiar serie para que se genere nueva
      })
    }));
  };

  const fileInputRef = React.useRef(null);
  const handlePickFile = () => fileInputRef.current?.click();
  const [openDocPreview, setOpenDocPreview] = useState(false);
  const [docPreviewSrc, setDocPreviewSrc] = useState(null);
  const [docPreviewType, setDocPreviewType] = useState('image');
  const docLabel = React.useMemo(() => {
    const doc = invoice?.invoiceDocuments;
    if (doc instanceof File) return doc.name;
    if (typeof doc === 'string') return doc.split('/').pop() || 'Documento';
    if (doc && typeof doc === 'object' && doc.invoiceDocuments) {
      const inner = doc.invoiceDocuments;
      if (inner instanceof File) return inner.name;
      if (typeof inner === 'string') return inner.split('/').pop() || 'Documento';
    }
    return 'Documento';
  }, [invoice?.invoiceDocuments]);

  const docPreviewSrcResolved = React.useMemo(() => {
    const preview = invoice?.invoiceDocumentsPreview;
    if (preview && typeof preview === 'string') return preview;
    const doc = invoice?.invoiceDocuments;
    if (doc instanceof File) {
      return preview || URL.createObjectURL(doc);
    }
    if (typeof doc === 'string') return absolutizeUrl(doc);
    if (doc && typeof doc === 'object' && doc.invoiceDocuments) {
      if (doc.invoiceDocuments instanceof File) {
        return preview || URL.createObjectURL(doc.invoiceDocuments);
      }
      if (typeof doc.invoiceDocuments === 'string') {
        return absolutizeUrl(doc.invoiceDocuments);
      }
    }
    return '';
  }, [invoice?.invoiceDocuments, invoice?.invoiceDocumentsPreview]);

  React.useEffect(() => {
    if (docPreviewSrcResolved && docPreviewSrcResolved.startsWith('blob:')) {
      return () => revokePreview(docPreviewSrcResolved);
    }
    return undefined;
  }, [docPreviewSrcResolved]);

  const revokePreview = (src) => { try { src && URL.revokeObjectURL(src); } catch { } };
  const clearDocument = () => {
    if (invoice.invoiceDocumentsPreview) revokePreview(invoice.invoiceDocumentsPreview);
    setInvoice(prev => ({ ...prev, invoiceDocuments: null, invoiceDocumentsPreview: null }));
    setDocPreviewSrc(null);
  };

  const compressImage = (file, maxDim = 1600, quality = 0.8) => new Promise((resolve) => {
    try {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.onload = () => {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) return resolve(file);
            const name = (file.name || 'document').replace(/\.(jpg|jpeg|png|gif|webp)$/i, '') + '.jpg';
            const compressed = new File([blob], name, { type: 'image/jpeg' });
            resolve(compressed);
          }, 'image/jpeg', quality);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    } catch {
      resolve(file);
    }
  });

  const handleFileChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (invoice.invoiceDocumentsPreview && invoice.invoiceDocumentsPreview.startsWith('blob:')) {
      revokePreview(invoice.invoiceDocumentsPreview);
    }
    let fileToStore = f;
    if (f.type && f.type.startsWith('image/')) {
      fileToStore = await compressImage(f);
    }
    const preview = URL.createObjectURL(fileToStore);
    setInvoice(prev => ({ ...prev, invoiceDocuments: fileToStore, invoiceDocumentsPreview: preview }));
  };

  const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000/').replace(/\/+$/, '/');
  const absolutizeUrl = (url) => {
    if (!url) return '';
    if (typeof url !== 'string') return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${API_BASE}${url.replace(/^\/+/, '')}`;
  };

  const handleOpenPreview = () => {
    const src = docPreviewSrcResolved;
    if (!src) return;
    setDocPreviewSrc(src);
    const t = (invoice.invoiceDocuments && invoice.invoiceDocuments.type) || '';
    const isPdf = (typeof src === 'string' && src.toLowerCase().includes('.pdf')) || t.includes('pdf');
    setDocPreviewType(isPdf ? 'pdf' : 'image');
    setOpenDocPreview(true);
  };

  const invoiceTypeCode = React.useMemo(() => {
    return (
      resolveCodeFromType(invoice?.invoiceType) ||
      resolveCodeFromType(invoice?.invoice_type) ||
      resolveCodeFromType(invoice?.invoice_type_id) ||
      invoice?.invoiceType ||
      'FC'
    );
  }, [invoice?.invoiceType, invoice?.invoice_type, invoice?.invoice_type_id, resolveCodeFromType]);

  const getStorageValue = React.useMemo(() => {
    if (invoice.invoice_type_id) {
      const foundById = (types || []).find(t => String(t.id) === String(invoice.invoice_type_id));
      if (foundById) return foundById;
    }
    if (invoice.invoice_type && invoice.invoice_type.id) {
      return invoice.invoice_type;
    }
    if (invoiceTypeCode) {
      return codeToType[invoiceTypeCode] || defaultInvoiceType;
    }
    return defaultInvoiceType;
  }, [codeToType, defaultInvoiceType, invoice.invoice_type, invoice.invoice_type_id, invoiceTypeCode, types]);

  const safeNumber = React.useMemo(() => (
    typeof invoice?.number === 'object' ? '' : (invoice?.number ?? '')
  ), [invoice?.number]);

  const safeSeries = React.useMemo(() => (
    typeof invoice?.series === 'object' ? '' : (invoice?.series ?? '')
  ), [invoice?.series]);

  const [invoiceLookupState, setInvoiceLookupState] = useState({
    number: '',
    exists: false,
    data: null,
  });
  const lookupCacheRef = useRef(new Map());
  const lookupRequestSeqRef = useRef(0);
  const normalizedLookupNumber = useMemo(() => String(safeNumber || '').trim(), [safeNumber]);

  useEffect(() => {
    if (invoiceTypeCode === 'V' || !normalizedLookupNumber) {
      setInvoiceLookupState({ number: '', exists: false, data: null });
      return;
    }

    const cacheKey = `${normalizedLookupNumber}::${invoice?.id || ''}`;
    if (lookupCacheRef.current.has(cacheKey)) {
      setInvoiceLookupState(lookupCacheRef.current.get(cacheKey));
      return;
    }

    const timeoutId = setTimeout(async () => {
      const requestSeq = ++lookupRequestSeqRef.current;
      try {
        const params = { number: normalizedLookupNumber };
        if (invoice?.id) params.exclude_id = invoice.id;
        const { data } = await API.get('invoice/lookup-number/', { params });
        if (requestSeq !== lookupRequestSeqRef.current) return;
        const next = {
          number: normalizedLookupNumber,
          exists: Boolean(data?.exists),
          data: data || null,
        };
        lookupCacheRef.current.set(cacheKey, next);
        setInvoiceLookupState(next);
      } catch {
        if (requestSeq !== lookupRequestSeqRef.current) return;
        setInvoiceLookupState({
          number: normalizedLookupNumber,
          exists: false,
          data: null,
        });
      }
    }, 430);

    return () => clearTimeout(timeoutId);
  }, [invoice?.id, invoiceTypeCode, normalizedLookupNumber]);

  const matchedInvoice = useMemo(() => {
    if (!invoiceLookupState.exists) return null;
    if (invoiceLookupState.number !== normalizedLookupNumber) return null;
    return invoiceLookupState.data || null;
  }, [invoiceLookupState, normalizedLookupNumber]);

  const matchedInvoiceStatusChip = useMemo(() => {
    const code = matchedInvoice?.invoice_status;
    if (code === 'ICF') return { label: 'Confirmada', color: 'success' };
    if (code === 'IFSC') return { label: 'Firmada sin confirmar', color: 'info' };
    if (code === 'ISSF') return { label: 'Pendiente de confirmar', color: 'warning' };
    if (matchedInvoice?.status) {
      const raw = String(matchedInvoice.status);
      const label = raw.charAt(0).toUpperCase() + raw.slice(1);
      return { label, color: 'default' };
    }
    return { label: matchedInvoice?.invoice_status_label || 'Existente', color: 'default' };
  }, [matchedInvoice]);

  const renderExistingInvoiceHint = useCallback(() => {
    if (!matchedInvoice) return null;
    const refLabel = `${matchedInvoice.number || ''}${matchedInvoice.series ? `-${matchedInvoice.series}` : ''}`;
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2.5, mt: -0.5 }}>
        <InfoOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          La factura {refLabel} ya existe en el sistema.
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={matchedInvoiceStatusChip.color}
          label={matchedInvoiceStatusChip.label}
        />
        {matchedInvoice?.payment_status_label && (
          <Chip
            size="small"
            variant="outlined"
            color="default"
            label={`Pago: ${matchedInvoice.payment_status_label}`}
          />
        )}
      </Stack>
    );
  }, [matchedInvoice, matchedInvoiceStatusChip.color, matchedInvoiceStatusChip.label]);

  const safeInvoiceTypeId = React.useMemo(() => {
    if (invoice?.invoice_type_id && typeof invoice.invoice_type_id === 'object') {
      return invoice.invoice_type_id?.id ?? '';
    }
    if (invoice?.invoice_type && invoice.invoice_type.id) {
      return invoice.invoice_type.id;
    }
    return invoice?.invoice_type_id ?? (getStorageValue?.id ?? '');
  }, [getStorageValue, invoice?.invoice_type, invoice?.invoice_type_id]);

  const hasValidInvoiceType = React.useMemo(() => {
    if (!hasInvoiceTypes) return false;
    return (types || []).some(t => String(t?.id) === String(safeInvoiceTypeId));
  }, [hasInvoiceTypes, safeInvoiceTypeId, types]);

  const sumDetailCents = React.useCallback((details) => {
    return (details || []).reduce((sum, item) => {
      const qty = Number(item?.quantity) || 0;
      const priceVal = item?.price ?? item?.finalPrice;
      const priceNum = Number(priceVal);
      const priceCents = Number.isFinite(priceNum) ? Math.round(priceNum * 100) : 0;
      return sum + qty * priceCents;
    }, 0);
  }, []);

  const formatCents = React.useCallback((cents) => (cents / 100).toFixed(2), []);

  const getSafeAdjustment = React.useCallback((value) => {
    if (value === '' || value == null) return 0;
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0;
  }, []);

  const computeBaseTotal = React.useCallback((details) => {
    return formatCents(sumDetailCents(details));
  }, [formatCents, sumDetailCents]);

  const formatCurrency = React.useCallback((amount) => {
    const num = Number(amount) || 0;
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }, []);

  const invoiceAdjustment = useMemo(
    () => getSafeAdjustment(invoice?.invoice_adjustment),
    [getSafeAdjustment, invoice?.invoice_adjustment]
  );
  const adjustmentOutOfRange = Math.abs(invoiceAdjustment) > 100;
  const computedTotalCents = useMemo(
    () => sumDetailCents(invoice?.invoice_detail || []),
    [invoice?.invoice_detail, sumDetailCents]
  );
  const adjustedTotalCents = useMemo(
    () => computedTotalCents + Math.round(invoiceAdjustment * 100),
    [computedTotalCents, invoiceAdjustment]
  );

  const buildReconciledPayload = useCallback((detailOverride, bonusOverride) => {
    const draftDetails = Array.isArray(detailOverride) ? detailOverride : (invoice?.invoice_detail || []);
    const draftBonuses = Array.isArray(bonusOverride) ? bonusOverride : (invoice?.invoice_bonus || []);
    const supplierId = invoice?.supplier_id ?? null;
    const detailsWithSupplier = draftDetails.map((line) => ({
      ...line,
      supplier_id: line?.supplier_id ?? supplierId,
    }));
    const bonusesWithSupplier = draftBonuses.map((line) => ({
      ...line,
      supplier_id: line?.supplier_id ?? supplierId,
    }));
    return buildReconciledInvoicePayload(detailsWithSupplier, bonusesWithSupplier, orders || []);
  }, [invoice?.invoice_bonus, invoice?.invoice_detail, invoice?.supplier_id, orders]);

  const logPayloadDiagnostics = useCallback((diagnostics) => {
    const debugEnabled = process.env.NODE_ENV === 'development'
      || process.env.REACT_APP_INVOICE_RECONCILIATION_DEBUG === 'true';
    if (!debugEnabled) return;
    (Array.isArray(diagnostics) ? diagnostics : []).forEach((diagnostic, index) => {
      console.debug(`[invoice-reconciliation] final payload line ${index + 1}`, diagnostic);
    });
  }, []);

  const getProductData = async () => {
    if (!invoice?.supplier_id || supplierAllProducts) {
      setProductsInvoice([]);
      return;
    }
    try {
      const response = await API.post('product/getProductsBySupplier/', { supplier_id: invoice.supplier_id });
      const productsWithIsAdd = response.data.map(product => ({
        ...product,
        isAdd: false,
      }));
      setProductsInvoice(productsWithIsAdd);
    } catch (error) {
      console.error(error);
    }
  };

  const handleNext = async () => {
    if (!isStepComplete()) return;

    if (activeStep === steps.length - 2) {
      // Logica especial para el penultimo paso (ej: enviar a API)
      const success = await handleComplete();
      if (success) setActiveStep(activeStep + 1);
      return;
    }

    setActiveStep(activeStep + 1);
  };

  const isStepComplete = React.useCallback(() => {
    if (!hasValidInvoiceType) return false;
    const supplierSelected = Boolean(values.supplierId || invoice?.supplier_id);
    const hasInvoiceDate = Boolean(invoice?.date || new Date().toISOString().split('T')[0]);
    const hasSeries = Boolean(String(invoice?.series || '').trim());
    const hasNumberIfRequired = invoiceTypeCode === 'V' || Boolean(String(invoice?.number || '').trim());
    switch (activeStep) {
      case 0:
        return (
          supplierSelected &&
          hasInvoiceDate &&
          hasSeries &&
          hasNumberIfRequired
        );
      case 1:
        return (invoice.invoice_detail?.length || 0) > 0;
      case 2:
        return invoiceHasLinkedNotes;
      case 3:
        return true;
      default:
        return false;
    }
  }, [activeStep, hasValidInvoiceType, invoice, invoiceTypeCode, invoiceHasLinkedNotes, values.supplierId]);

  const handleBack = () => {
    // if (completed[activeStep]) return;
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };


  const handleComplete = async () => {
    try {
      const maxLength = 30;
      const normalizedNumber = typeof invoice?.number === 'string'
        ? invoice.number.trim()
        : String(invoice?.number ?? '').trim();

      if (!normalizedNumber && invoiceTypeCode !== 'V') {
        setSnack({
          open: true,
          message: 'Numero requerido',
          severity: 'error',
        });
        return false;
      }

      if (normalizedNumber && normalizedNumber.length > maxLength) {
        setSnack({
          open: true,
          message: 'El número de factura es inválido o demasiado largo (máx 30 caracteres).',
          severity: 'error',
        });
        return false;
      }

      const invoiceAdjustmentValue = getSafeAdjustment(invoice?.invoice_adjustment);
      if (Math.abs(invoiceAdjustmentValue) > 100) {
        setSnack({
          open: true,
          message: 'El ajuste no puede exceder 100.00',
          severity: 'error',
        });
        return false;
      }

      setLoading(true);

      // Recolectar IDs �nicos de �rdenes desde los detalles
      const { payloadLines, orderIds, reviewWarnings, payloadDiagnostics } = buildReconciledPayload();
      logPayloadDiagnostics(payloadDiagnostics);
      if (reviewWarnings.length > 0) {
        setSnack({
          open: true,
          message: 'Existe una orden vigente compatible para esta linea, pero no se pudo conciliar. Revise la linea antes de guardar.',
          severity: 'error',
        });
        return false;
      }

      const linkedNotes = getLinkedNotesForInvoice(invoice, notes, DRAFT_INVOICE_NOTE_LINK_ID);
      if (linkedNotes.length === 0) {
        setSnack({
          open: true,
          message: 'Sin notas vinculadas; puedes saltar este paso.',
          severity: 'warning',
        });
        return false;
      }

      // Formatear notas (normalizar fecha a ISO si existe)
      const formattedNotes = linkedNotes.map((note) =>
        buildNotePayload(note, logicalWarehouses)
      );

      // Determinar estado a enviar
      const statusToSend = 'ICF';

      // Ensamblar payload final para el API
      const formattedInvoice = {
        ...invoice,
        invoice_detail: (payloadLines.invoice_detail || []).map((it) => ({
          ...it,
          quantityRe: Number(it?.quantityRe || 0),
        })),
        invoice_bonus: (payloadLines.invoice_bonus || []).map((it) => ({
          ...it,
          quantityRe: Number(it?.quantityRe || 0),
        })),
        date: toISODate(invoice?.date) || toISODate(new Date()),
        paymentType: invoice?.paymentType ? 'Credito' : 'Contado',
        invoiceStatus: statusToSend,          // <-- usar status calculado
        orders: orderIds,                     // <-- mantener 'orders' como en tu backend
        notes: formattedNotes,
      };
      if (normalizedNumber) {
        formattedInvoice.number = normalizedNumber;
      } else {
        delete formattedInvoice.number;
      }
      formattedInvoice.invoice_adjustment = invoiceAdjustmentValue.toFixed(2);
      formattedInvoice.total = computeBaseTotal(formattedInvoice.invoice_detail);

      // No enviar archivos en el POST inicial (se suben con patch)
      delete formattedInvoice.invoiceDocuments;
      delete formattedInvoice.invoiceDocumentsPreview;
      delete formattedInvoice.paymentStatus;

      // El backend no debe recibir 'id' en creaci�n
      if ('id' in formattedInvoice) delete formattedInvoice.id;

      // Crear factura
      const response = await API.post('invoice/createInvoice/', formattedInvoice);
      const createdId = response?.data?.invoice?.id;
      if (createdId && linkedNotes.length > 0) {
        linkNotesToInvoice(createdId);
      }

      // Subir documento(s) si corresponde
      const hasFileCtor = typeof File !== 'undefined';
      const fileToUpload = invoice?.invoiceDocuments instanceof File
        ? invoice.invoiceDocuments
        : (invoice?.invoiceDocuments && invoice.invoiceDocuments.invoiceDocuments instanceof File
          ? invoice.invoiceDocuments.invoiceDocuments
          : null);
      if (createdId && hasFileCtor && fileToUpload) {
        const fd = new FormData();
        fd.append('invoiceDocuments', fileToUpload);
        await API.patch(`invoice/${createdId}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setCompleted(prev => ({ ...prev, [activeStep]: true }));
      setArrivedViaSkip(false);

      return true;
    } catch (error) {
      const errorData = error?.response?.data;
      console.error('Respuesta de error:', error?.response?.data);

      let errorMessage = 'Error al crear factura';
      if (Array.isArray(errorData?.number) && errorData.number.length) {
        errorMessage = errorData.number.join(' ');
      } else if (typeof errorData?.message === 'string') {
        errorMessage = errorData.message;
      } else if (errorData?.message && typeof errorData.message === 'object') {
        const firstKey = Object.keys(errorData.message)[0];
        const val = errorData.message[firstKey];
        errorMessage = Array.isArray(val) ? val.join(' ') : String(val || errorMessage);
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setSnack({
        open: true,
        message: errorMessage,
        severity: 'error',
      });

      return false;
    } finally {
      setLoading(false);
    }
  };



  const handleSkip = async () => {
    try {
      const maxLength = 30;
      const normalizedNumber = typeof invoice?.number === 'string'
        ? invoice.number.trim()
        : String(invoice?.number ?? '').trim();

      if (!normalizedNumber && invoiceTypeCode !== 'V') {
        setSnack({
          open: true,
          message: 'Numero requerido',
          severity: 'error',
        });
        return false;
      }

      if (normalizedNumber && normalizedNumber.length > maxLength) {
        setSnack({
          open: true,
          message: 'El número de factura es inválido o demasiado largo (máx 30 caracteres).',
          severity: 'error',
        });
        return false;
      }

      const invoiceAdjustmentValue = getSafeAdjustment(invoice?.invoice_adjustment);
      if (Math.abs(invoiceAdjustmentValue) > 100) {
        setSnack({
          open: true,
          message: 'El ajuste no puede exceder 100.00',
          severity: 'error',
        });
        return false;
      }

      setLoading(true);

      // 1) Factura temporal: quitar firmante
      const tempInvoice = {
        ...invoice,
        signedById: null,
      };

      // 3) Recolectar orderIds �nicos
      const { payloadLines, orderIds, reviewWarnings, payloadDiagnostics } = buildReconciledPayload(tempInvoice.invoice_detail, tempInvoice.invoice_bonus);
      logPayloadDiagnostics(payloadDiagnostics);
      if (reviewWarnings.length > 0) {
        setSnack({
          open: true,
          message: 'Existe una orden vigente compatible para esta linea, pero no se pudo conciliar. Revise la linea antes de guardar.',
          severity: 'error',
        });
        return false;
      }
      const skippedDetails = (payloadLines.invoice_detail || []).map((item) => ({
        ...item,
        quantityRe: 0,
      }));
      const skippedBonuses = (payloadLines.invoice_bonus || []).map((item) => ({
        ...item,
        quantityRe: 0,
      }));

      // 4) Formatear notas (normalizar fecha a ISO)
      const formattedNotes = (notes ?? []).map((note) =>
        buildNotePayload(note, logicalWarehouses)
      );

      // 5) Determinar estado seg�n notas / firmante original
      const statusToSend = 'ISSF';

      // 6) Armar payload para el API (similar a handleComplete)
      const formattedInvoice = {
        ...tempInvoice,
        invoice_detail: skippedDetails,
        invoice_bonus: skippedBonuses,
        date: toISODate(invoice?.date) || toISODate(new Date()),
        paymentType: invoice?.paymentType ? 'Credito' : 'Contado',
        invoiceStatus: statusToSend,
        orders: orderIds,
        notes: formattedNotes,
      };
      if (normalizedNumber) {
        formattedInvoice.number = normalizedNumber;
      } else {
        delete formattedInvoice.number;
      }
      formattedInvoice.invoice_adjustment = invoiceAdjustmentValue.toFixed(2);
      formattedInvoice.total = computeBaseTotal(formattedInvoice.invoice_detail);

      delete formattedInvoice.invoiceDocuments;
      delete formattedInvoice.invoiceDocumentsPreview;
      delete formattedInvoice.paymentStatus;

      // No mandar id en creaci�n
      if ('id' in formattedInvoice) delete formattedInvoice.id;

      // 7) Crear en backend
      const response = await API.post('invoice/createInvoice/', formattedInvoice);
      const createdId = response?.data?.invoice?.id;
      if (createdId && (notes || []).length > 0) {
        linkNotesToInvoice(createdId);
      }

      // 8) Actualizar UI y avanzar
      setArrivedViaSkip(true);
      setInvoice((prev) => ({
        ...prev,
        ...tempInvoice,
        invoice_detail: skippedDetails,
        invoice_bonus: skippedBonuses,
      }));
      setCompleted(prev => ({ ...prev, [activeStep]: true }));
      setActiveStep(prev => prev + 1);

      return true;
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = 'Error al crear factura';

      if (Array.isArray(errorData?.number) && errorData.number.length) {
        errorMessage = errorData.number.join(' ');
      } else if (typeof errorData?.message === 'string') {
        errorMessage = errorData.message;
      } else if (errorData?.message && typeof errorData.message === 'object') {
        const firstKey = Object.keys(errorData.message)[0];
        const val = errorData.message[firstKey];
        errorMessage = Array.isArray(val) ? val.join(' ') : String(val || errorMessage);
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setSnack({
        open: true,
        message: errorMessage,
        severity: 'error',
      });

      return false;
    } finally {
      setLoading(false);
    }
  };




  const handleReset = useCallback(() => {
    setActiveStep(0);
    setInvoice({
      date: new Date().toISOString(),
      total: 0,
      invoiceType: 'FC',
      invoice_type: facturaCambiariaType || null,
      invoice_type_id: facturaCambiariaType?.id ?? null,
      number: '',
      series: '',
      paymentType: true,
      paymentStatus: PAYMENT_STATUS.PENDING,
      supplier_id: 0,
      invoice_adjustment: 0,
      invoice_detail: [],
      invoice_bonus: [],
      invoiceDocuments: null,
    });

    // Limpiar localStorage
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch { }

    // Resetear arrays
    setSelectedProducts([]);
    setProductsInvoice([]);
    setDetailUnified(null);
    setDetailLoading(false);
    setDetailCacheKey(null);
    setNotes([]);
    setArrivedViaSkip(false);

    if (resetFlow) resetFlow();
  }, [facturaCambiariaType, resetFlow]);

  const handleCloseDetailModal = useCallback(() => {
    setOpen2(false);
    setDetailUnified(null);
    setDetailLoading(false);
    setDetailCacheKey(null);
    setSelectedProducts([]);
  }, [setSelectedProducts]);

  const handleUnifyProducts = async () => {
    if (selectionModel.length === 0) return;
    const selectedOrderIds = selectionModel
      .map(id => parseInt(id.toString().replace(/[^\d]/g, ""), 10))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => a - b)
      .join(",");

    setDetailCacheKey(selectedOrderIds);
    setDetailUnified(null);
    setDetailLoading(true);
    setLoading(true);
    setOpen2(true);
    try {
      const params = { order_ids: selectedOrderIds, t: Date.now() };
      const res = await API.get('/order-Av/unified_products/', { params });
      const unifiedProducts = res.data;
      setDetailUnified(unifiedProducts);
    } catch (error) {
      console.error('Error unificando productos', error);
      setDetailUnified(null);
      setSnack({
        open: true,
        message: 'Error al unificar productos',
        severity: 'error',
      });
    } finally {
      setDetailLoading(false);
      setLoading(false);
    }
  };

  const createManualDetailRowId = React.useCallback((productId, index) => {
    return `manual-${productId ?? 'x'}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
  }, []);

  const addToInvoiceDetails = (productsToAdd) => {
    if (!productsToAdd || productsToAdd.length === 0) return;

    const newInvoiceDetails = buildManualInvoiceDetailLines(productsToAdd, invoice?.supplier_id, {
      createRowId: createManualDetailRowId,
    });

    const combined = [...(invoice?.invoice_detail || []), ...newInvoiceDetails];
    const totalFormatted = computeBaseTotal(combined);

    setInvoice({
      ...invoice,
      invoice_detail: combined,
      total: totalFormatted,
    });
  };

  const handleAddToInvoice = () => {
    if (!selectedProducts || selectedProducts.length === 0) return;

    addToInvoiceDetails(selectedProducts);

    const updatedProducts = productsInvoice.map(product => {
      const pid = Number(product?.id);
      const found = selectedProducts.some(p => Number(p?.id) === pid);
      return found ? { ...product, isAdd: true } : product;
    });
    setProductsInvoice(updatedProducts);
    setSelectedProducts([]);
    setOpenInvoice(false);
  };


  useEffect(() => {
    if (invoice.supplier_id !== 0 && activeStep === 1 && !supplierAllProducts) {
      getProductData();
      return;
    }
    if (invoice.supplier_id !== 0 && activeStep === 1 && supplierAllProducts) {
      setProductsInvoice([]);
    }
  }, [invoice.supplier_id, activeStep, supplierAllProducts]);

  useEffect(() => {
    if (!invoice?.supplier_id) {
      setSupplierAllProducts(false);
      setProductsInvoice([]);
    }
  }, [invoice?.supplier_id]);


  useEffect(() => {
    const stepCompleted = isStepComplete();
    if (completed[activeStep] !== stepCompleted) {
      setCompleted(prev => ({
        ...prev,
        [activeStep]: stepCompleted
      }));
    }
  }, [activeStep, isStepComplete, completed]);

  const summary = useMemo(() => {
    if (!invoice || !invoice.invoice_detail) return null;

    let subTotal = 0;
    let descuentoProductoNoEntregado = 0;
    const adjustment = getSafeAdjustment(invoice?.invoice_adjustment);
    const labelTotal = 'Total a pagar';


    invoice.invoice_detail.forEach((item) => {
      const qty = parseInt(item.quantity) || 0;
      const qtyRe = parseInt(item.quantityRe) || 0;
      const qtyReClamped = qtyRe < qty ? qtyRe : qty;
      const price = parseFloat(item.price ?? item.finalPrice) || 0;

      subTotal += qty * price;
      descuentoProductoNoEntregado += (qty - qtyReClamped) * price;
    });

    return {
      subTotal,
      descuentoProductoNoEntregado,
      invoiceAdjustment: adjustment,
      total: subTotal - descuentoProductoNoEntregado + adjustment,
      labelTotal: labelTotal,
    };
  }, [getSafeAdjustment, invoice?.invoice_adjustment, invoice.invoice_detail]);

  // Bonus agrupados por producto para render consistente
  const bonusByParent = useMemo(() => {
    const map = new Map();
    (invoice?.invoice_bonus || []).forEach((b) => {
      const pid = b.product_id;
      if (pid == null) return;
      const arr = map.get(pid) || [];
      arr.push(b);
      map.set(pid, arr);
    });
    return map;
  }, [invoice?.invoice_bonus]);

  const detailWithBonuses = useMemo(() => {
    return (invoice?.invoice_detail || []).map((row) => ({
      ...row,
      _bonuses: bonusByParent.get(row.product_id) || [],
    }));
  }, [invoice?.invoice_detail, bonusByParent]);

  const handleAddPromotionsToInvoice = () => {
    if (selectedPromotions.length === 0) return;

    // Crear una copia del invoice
    const updatedInvoice = { ...invoice };

    // Construir bonificaciones con estructura esperada por el serializer
    const newBonuses = selectedPromotions.map(product => normalizeInvoiceLineFormat({
      clientRowId: `manual-b-${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      product_id: product.id,
      product: product,
      supplier_id: product?.supplier_id ?? product?.supplier?.id ?? invoice?.supplier_id ?? null,
      description: product.description,
      code: product.code,
      quantity: 1,
      quantityRe: 1,
      checked: true,
      prformat: product.prformat || '',
      dueDate: null,
      old_taken: 0,
      source_orders: [],
      order_bonus_id: null // Porque es una bonificacion manual
    }));

    // Asegurar que invoice_bonus existe
    if (!updatedInvoice.invoice_bonus) {
      updatedInvoice.invoice_bonus = [];
    }

    // Agregar al array global de bonificaciones
    updatedInvoice.invoice_bonus = [
      ...updatedInvoice.invoice_bonus,
      ...newBonuses
    ];

    // Actualizar estado
    setInvoice(updatedInvoice);
    setSelectedPromotions([]);
    setOpenPromotionModal(false);
  };



  const getStepContent = (step) => {
    if (manualOrderSelectionEnabled && values.hasOrder) {
      switch (step) {
        case 0:
          return (
            <Box sx={{ marginTop: '15px' }}>

              <Stack direction={{ sx: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
                {invoiceTypeCode === 'V' ? (
                  <VoucherButton
                    supplierId={invoice.supplier_id}
                    onCodeGenerated={(newCode) => setInvoice(prev => ({ ...prev, series: newCode }))}
                    initialCode={invoice.series}
                    disabled={!invoice.supplier_id}
                  />
                ) : (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <TextBox
                        label='Numero de factura'
                        icon={Numbers}
                        type='number'
                        name={'number'}
                        onChange={handleChange}
                        maxLength={12}
                        newValue={safeNumber}
                        helperText={matchedInvoice ? 'Este número ya está registrado.' : ''}
                      />
                      {renderExistingInvoiceHint()}
                    </Box>
                    <TextBox
                      label='Numero de serie'
                      icon={Abc}
                      type='text'
                      name={'series'}
                      maxLength={15}
                      onChange={handleChange}
                      forceUppercase={true}
                      newValue={safeSeries}
                    />
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                <Tooltip title={invoice.invoiceDocuments ? 'Cambiar documento' : 'Adjuntar imagen o PDF'} arrow>
                  <div style={{ display: 'inline-block' }}>
                    {/* Micro-interacciones (usa framer-motion ya que lo tienes en deps) */}
                    <motion.div
                      whileHover={{ y: -1, scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ display: 'inline-block' }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        badgeContent={
                          invoice.invoiceDocuments ? (
                            <CheckCircleRounded
                              fontSize="small"
                              sx={{
                                color: 'success.main',
                                bgcolor: 'common.white',
                                borderRadius: '50%',
                                boxShadow: 1,
                              }}
                            />
                          ) : null
                        }
                      >
                        <IconButton
                          onClick={handlePickFile}
                          size="medium"
                          aria-label={invoice.invoiceDocuments ? 'Cambiar documento' : 'Adjuntar documento'}
                          sx={(theme) => {
                            const isAttached = Boolean(invoice.invoiceDocuments);
                            const primaryGrad = 'linear-gradient(135deg, #10173c 0%, #10173c 100%)'; // corporativo azul
                            const successGrad = 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'; // corporativo verde

                            return {
                              position: 'relative',
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              px: 0,
                              // Gradiente sutil corporativo
                              background: isAttached ? successGrad : primaryGrad,
                              color: 'common.white',
                              boxShadow: isAttached ? '0 6px 14px rgba(46,125,50,.25)' : '0 6px 14px rgba(21,101,192,.25)',
                              border: '1px solid',
                              borderColor: 'rgba(255,255,255,.18)',
                              backdropFilter: 'saturate(120%)',
                              backgroundSize: '200% 200%',
                              transition: 'background-position .35s ease, box-shadow .25s ease, transform .2s ease',
                              '&:hover': {
                                backgroundPosition: 'right center',
                                boxShadow: isAttached
                                  ? '0 10px 20px rgba(46,125,50,.30)'
                                  : '0 10px 20px rgba(21,101,192,.30)',
                              },
                              '&:active': {
                                transform: 'translateY(0.5px)',
                              },
                              // Anillo de foco accesible (teclado)
                              '&:focus-visible': {
                                outline: 'none',
                                boxShadow: isAttached
                                  ? `0 0 0 3px ${theme.palette.success.light}`
                                  : `0 0 0 3px ${theme.palette.primary.light}`,
                              },
                            };
                          }}
                        >
                          <UploadFile fontSize="small" />
                        </IconButton>
                      </Badge>
                    </motion.div>
                  </div>
                </Tooltip>

                {docPreviewSrcResolved && (
                  <>
                    <Tooltip title="Ver documento">
                      <IconButton color='primary' onClick={handleOpenPreview}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Chip size="small" label={docLabel} onDelete={clearDocument} />
                  </>
                )}
                <CustomSwitch
                  name="paymentType"
                  label={invoice.paymentType ? 'Credito' : 'Contado'}
                  initialValue={invoice.paymentType}
                  onChange={(name, value) => setInvoice(prev => ({ ...prev, [name]: value }))}
                />
              </Stack>

              <Stack direction={{ sx: 'column', sm: 'row' }} spacing={2} sx={{ mb: 0 }}>
                <CustomAutoComplete
                  name="supplier_id"
                  onChange={handleSupplierChange}
                  getOptionLabel={(option) => option.companyName}
                  supplier={invoice.supplier_id}
                  endpoint="suppliers/minimal/"
                  onSelectOption={(supplier) => setSupplierAllProducts(Boolean(supplier?.all_products))}
                  forceDisabled={invoice.invoice_detail?.length > 0}
                />
                {/* 1*/}
                <InvoiceTypeSelect
                  types={types}
                  hasInvoiceTypes={hasInvoiceTypes}
                  safeInvoiceTypeId={safeInvoiceTypeId}
                  onSelect={(selected) => handleSelectChange('invoiceType', selected)}
                />
                <DatePickV2
                  allowFutureDates={false}
                  allowHistoricalDates={true}
                  label='Fecha factura'
                  size='small'
                  name={'date'}
                  onChange={handleChange}
                  value={invoice.date || new Date().toISOString()}
                  returnType='iso'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size='large'
                      icon={<InsertDriveFile />}
                      checkedIcon={<Task />}
                      checked={values.persistHasOrder ?? values.hasOrder}
                      onChange={handleCheckboxChange}
                      disabled={!values.supplierId || invoice.invoice_detail?.length > 0}
                    />
                  }
                  label="¿Tiene orden?"
                  sx={{ color: 'gray' }}
                  labelPlacement="end"
                />


                <Typography variant="caption" color="textSecondary">
                  {values.supplierId
                    ? ''
                    : 'Sin proveedor seleccionado'}
                </Typography>
                {values.hasOrder && (
                  <IconButton
                    color='success'
                    sx={{ width: '50px', position: 'relative' }}
                    size='large'
                    onClick={handleUnifyProducts}
                    disabled={selectionModel.length === 0}
                  >
                    <FormatListBulleted />

                    {(selectionModel.length > 0 || invoice.invoice_detail?.length > 0) && (
                      <Fade in={true} timeout={300}>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {invoice.invoice_detail?.length > 0 ? (
                            <Check sx={{ fontSize: 16 }} />
                          ) : (
                            selectionModel.length
                          )}
                        </Box>
                      </Fade>
                    )}
                  </IconButton>
                )}
              </Stack>
              {values.hasOrder ? (
                loading ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cargando ordenes...
                    </Typography>
                  </Box>
                ) : orders && orders.length > 0 ? (
                  <Box sx={{ height: 350, width: '100%' }}>
                    <DataGrid
                      rows={orders}
                      columns={columns}
                      loading={loading}
                      checkboxSelection
                      disableColumnFilter
                      disableColumnMenu
                      disableSelectionOnClick
                      isRowSelectable={() => true}
                      getRowId={(row) => row.id ?? row.gridId}
                      rowSelectionModel={selectionModel}
                      onRowSelectionModelChange={onSelectionModelChange}
                      pageSizeOptions={[5, 10]}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 5 },
                        },
                      }}
                      sx={{
                        '& .MuiDataGrid-cell': {
                          padding: '8px',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay ordenes disponibles para este proveedor
                    </Typography>
                    {orders && orders.length === 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={updateOrderList}
                        sx={{ mt: 1 }}
                      >
                        Reintentar carga
                      </Button>
                    )}
                  </Box>
                )
              ) : null}
              <CustomModal
                open={open}
                setOpen={setOpen}
                handleClose={() => setOpen(false)}
                component={EditOrder}
                width='70%'
                title='Editar detalle de la orden'
                icon={EditNote}
                componentProps={{
                  setOpen: setOpen,
                  order: selectedOrder,
                  updateOrder: updateOrderList,
                  setOrder: setSelectedOrder
                }}
              />
              <CustomModal
                open={open2}
                setOpen={setOpen2}
                handleClose={handleCloseDetailModal}
                component={AddInvoiceDetail}
                width='70%'
                title='Ingresar cantidades'
                icon={Add}
                componentProps={{
                  key: detailModalKey,
                  setOpen: setOpen2,
                  orderUnified: detailUnified,
                  detailLoading: detailLoading,
                  setInvoice: setInvoice,
                  invoice: invoice
                }}
              />
              <CustomModal
                open={openDocPreview}
                setOpen={setOpenDocPreview}
                handleClose={() => setOpenDocPreview(false)}
                component={DocumentPreview}
                width='60%'
                title='Documento adjunto'
                icon={Image}
                componentProps={{ src: docPreviewSrc || docPreviewSrcResolved, type: docPreviewType, onDelete: clearDocument }}
              />


            </Box>
          );
        case 1:
          return (
            <Box sx={{ marginTop: '15px' }}>


              <Typography>Agregar datos de ingreso con orden de compra</Typography>
              <ErrorBoundary>
                <ButtonNotes
                  notes={notes}
                  setNotes={setNotes}
                  setInvoice={setInvoice}
                  invoice={invoice}
                  selectedInvoices={noteTargetInvoices}
                  logicalWarehouses={logicalWarehouses}
                  canUseLogical={canUseLogical}
                />
              </ErrorBoundary>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                {docPreviewSrcResolved && (
                  <>
                    <Tooltip title="Ver documento">
                      <IconButton color='primary' onClick={handleOpenPreview}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Chip size="small" label={docLabel} onDelete={clearDocument} />
                  </>
                )}
              </Box>
              <AdjustmentBox
                value={invoice?.invoice_adjustment}
                onChange={handleAdjustmentChange}
                computedTotalCents={computedTotalCents}
                adjustedTotalCents={adjustedTotalCents}
                outOfRange={adjustmentOutOfRange}
                formatCurrency={formatCurrency}
              />

              <CollapsedProductsTable
                data={(invoice.invoice_detail || []).map(item => ({
                  ...item,
                  description: typeof item.description === 'string' ? item.description :
                    (item.description?.description || item.description?.name || ''),
                  code: typeof item.code === 'string' ? item.code : item.code?.code || '',
                  prformat: typeof item.prformat === 'string' ? item.prformat :
                    (item.prformat?.name || item.prformat?.prformat || ''),
                  price: typeof item.price === 'number' ? item.price :
                    (item.price?.price || Number(item.price) || 0),
                  finalPrice: typeof item.finalPrice === 'number' ? item.finalPrice :
                    (item.finalPrice?.price || Number(item.finalPrice) || 0),
                }))}
                columns={columnsSummary}
                renderExtras={renderExtras}
                pagination={true}
                rowsPerPage={5}
                summary={summary}
              />
            </Box>
          );
        case 2:
          return (
            <Box sx={{ marginTop: '15px' }}>
              <SuccessMessage
                onNewInvoice={handleReset}
                success={showSuccessMessage ? "Factura registrada con exito!" : undefined}
                subtitle={"Listo, ahora puedes verlo desde el listado de facturas."}
                alert={arrivedViaSkip
                  ? "Factura no confirmada, Para pagarla debes confirmarla."
                  : showNoNotesAlert ? "Factura registrada sin notas de entrada." : undefined}
              />

            </Box>
          );
        default:
          return <Typography>Paso no implementado</Typography>;
      }
    } else {
      switch (step) {
        case 0: // Datos de factura
          return (
            <Box sx={{ marginTop: '15px' }}>

              <Stack direction={{ sx: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
                {invoiceTypeCode === 'V' ? (
                  <VoucherButton
                    supplierId={invoice.supplier_id}
                    onCodeGenerated={(newCode) => setInvoice(prev => ({ ...prev, series: newCode }))}
                    initialCode={invoice.series}
                    disabled={!invoice.supplier_id}
                  />
                ) : (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <TextBox
                        label='Numero de factura'
                        icon={Numbers}
                        type='number'
                        name={'number'}
                        onChange={handleChange}
                        maxLength={12}
                        newValue={safeNumber}
                        helperText={matchedInvoice ? 'Este número ya está registrado.' : ''}
                      />
                      {renderExistingInvoiceHint()}
                    </Box>
                    <TextBox
                      label='Numero de serie'
                      icon={Abc}
                      type='text'
                      name={'series'}
                      maxLength={15}
                      onChange={handleChange}
                      forceUppercase={true}
                      newValue={safeSeries}
                    />
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                <Tooltip title={invoice.invoiceDocuments ? 'Cambiar documento' : 'Adjuntar imagen o PDF'} arrow>
                  <div style={{ display: 'inline-block' }}>
                    {/* Micro-interacciones (usa framer-motion ya que lo tienes en deps) */}
                    <motion.div
                      whileHover={{ y: -1, scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ display: 'inline-block' }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        badgeContent={
                          invoice.invoiceDocuments ? (
                            <CheckCircleRounded
                              fontSize="small"
                              sx={{
                                color: 'success.main',
                                bgcolor: 'common.white',
                                borderRadius: '50%',
                                boxShadow: 1,
                              }}
                            />
                          ) : null
                        }
                      >
                        <IconButton
                          onClick={handlePickFile}
                          size="medium"
                          aria-label={invoice.invoiceDocuments ? 'Cambiar documento' : 'Adjuntar documento'}
                          sx={(theme) => {
                            const isAttached = Boolean(invoice.invoiceDocuments);
                            const primaryGrad = 'linear-gradient(135deg, #10173c 0%, #10173c 100%)'; // corporativo azul
                            const successGrad = 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'; // corporativo verde

                            return {
                              position: 'relative',
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              px: 0,
                              // Gradiente sutil corporativo
                              background: isAttached ? successGrad : primaryGrad,
                              color: 'common.white',
                              boxShadow: isAttached ? '0 6px 14px rgba(46,125,50,.25)' : '0 6px 14px rgba(21,101,192,.25)',
                              border: '1px solid',
                              borderColor: 'rgba(255,255,255,.18)',
                              backdropFilter: 'saturate(120%)',
                              backgroundSize: '200% 200%',
                              transition: 'background-position .35s ease, box-shadow .25s ease, transform .2s ease',
                              '&:hover': {
                                backgroundPosition: 'right center',
                                boxShadow: isAttached
                                  ? '0 10px 20px rgba(46,125,50,.30)'
                                  : '0 10px 20px rgba(21,101,192,.30)',
                              },
                              '&:active': {
                                transform: 'translateY(0.5px)',
                              },
                              // Anillo de foco accesible (teclado)
                              '&:focus-visible': {
                                outline: 'none',
                                boxShadow: isAttached
                                  ? `0 0 0 3px ${theme.palette.success.light}`
                                  : `0 0 0 3px ${theme.palette.primary.light}`,
                              },
                            };
                          }}
                        >
                          <UploadFile fontSize="small" />
                        </IconButton>
                      </Badge>
                    </motion.div>
                  </div>
                </Tooltip>

                {docPreviewSrcResolved && (
                  <>
                    <Tooltip title="Ver documento">
                      <IconButton color='primary' onClick={handleOpenPreview}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Chip size="small" label={docLabel} onDelete={clearDocument} />
                  </>
                )}
                <CustomSwitch
                  name="paymentType"
                  label={invoice.paymentType ? 'Credito' : 'Contado'}
                  initialValue={invoice.paymentType}
                  onChange={(name, value) => setInvoice(prev => ({ ...prev, [name]: value }))}
                />
              </Stack>

              <Stack direction={{ sx: 'column', sm: 'row' }} spacing={2} sx={{ mb: 0 }}>
                <CustomAutoComplete
                  name="supplier_id"
                  onChange={handleSupplierChange}
                  getOptionLabel={(option) => option.companyName}
                  supplier={invoice.supplier_id}
                  endpoint="suppliers/minimal/"
                  onSelectOption={(supplier) => setSupplierAllProducts(Boolean(supplier?.all_products))}
                  forceDisabled={invoice.invoice_detail?.length > 0}
                />
                <InvoiceTypeSelect
                  types={types}
                  hasInvoiceTypes={hasInvoiceTypes}
                  safeInvoiceTypeId={safeInvoiceTypeId}
                  onSelect={(selected) => handleSelectChange('invoiceType', selected)}
                />
                <DatePickV2
                  allowFutureDates={false}
                  allowHistoricalDates={true}
                  label='Fecha factura'
                  size='small'
                  name={'date'}
                  onChange={handleChange}
                  value={invoice.date || new Date().toISOString()}
                  returnType='iso'
                />
                <Typography variant="caption" color="textSecondary">
                  {values.supplierId
                    ? 'Las ordenes abiertas se usan como candidatas para conciliacion automatica por linea.'
                    : 'Sin proveedor seleccionado'}
                </Typography>
              </Stack>
              {values.supplierId ? (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1 }} alignItems="center">
                  {loading ? (
                    <Chip size="small" variant="outlined" label="Cargando órdenes candidatas..." />
                  ) : Array.isArray(orders) && orders.length > 0 ? (
                    <Chip size="small" color="info" variant="outlined" label={`Órdenes candidatas detectadas: ${orders.length}`} />
                  ) : (
                    <Chip size="small" variant="outlined" label="Sin órdenes candidatas: se permitirá factura sin OC" />
                  )}
                </Stack>
              ) : null}

              <GeneralModal
                open={open}
                setOpen={setOpen}
                handleClose={() => setOpen(false)}
                component={EditOrder}
                wdth='70%'
                componentProps={{
                  setOpen: setOpen,
                  order: selectedOrder,
                  updateOrder: updateOrderList,
                  setOrder: setSelectedOrder
                }}
              />
              <CustomModal
                open={open2}
                setOpen={setOpen2}
                handleClose={handleCloseDetailModal}
                component={AddInvoiceDetail}
                width='70%'
                title='Ingresar cantidades'
                icon={Add}
                componentProps={{
                  key: detailModalKey,
                  setOpen: setOpen2,
                  orderUnified: detailUnified,
                  detailLoading: detailLoading,
                  setInvoice: setInvoice,
                  invoice: invoice
                }}
              />


            </Box>
          );
        case 1: // Agregar productos
          return (
            <Box sx={{ marginTop: '15px' }}>
              <CustomTableInvoice
                setOpen={setOpenInvoice}
                invoice={invoice}
                setInvoice={setInvoice}
                products={productsInvoice}
                setProducts={setProductsInvoice}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                setCurrentDetailIndex={setCurrentDetailIndex}
                setOpenPromotionModal={setOpenPromotionModal}
                candidateOrders={orders}
                initialSearchTerm={initialSearchTerm}
                onSearchTermChange={onSearchTermChange}
              />
              <CustomModal
                open={openInvoice}
                setOpen={setOpenInvoice}
                handleClose={() => setOpenInvoice(false)}
                component={PickProduct}
                width='70%'
                title='Agregar productos'
                icon={Add}
                componentProps={{
                  products: productsInvoice,
                  selectedProducts: selectedProducts,
                  setSelectedProducts: setSelectedProducts,
                  handleAddToCart: handleAddToInvoice,
                  excludedProductIds: invoiceProductIdSet,
                  supplierId: invoice.supplier_id,
                  allProducts: supplierAllProducts,
                }}
              />
              <CustomModal
                open={openPromotionModal}
                setOpen={setOpenPromotionModal}
                handleClose={() => setOpenPromotionModal(false)}
                component={PickPromotion}
                width='70%'
                title='Agregar promoción'
                icon={Discount}
                componentProps={{
                  products: productsInvoice,
                  selectedPromotions: selectedPromotions,
                  handleAddPromotions: handleAddPromotionsToInvoice,
                  setSelectedPromotions: setSelectedPromotions,
                }}
              />
            </Box>
          );
        case 2: // Confirmar
          return (
            <Box sx={{ marginTop: '15px' }}>
              <Typography>Dato de ingreso sin orden de compra</Typography>
              <ErrorBoundary>
                <ButtonNotes
                  notes={notes}
                  setNotes={setNotes}
                  setInvoice={setInvoice}
                  invoice={invoice}
                  selectedInvoices={noteTargetInvoices}
                  logicalWarehouses={logicalWarehouses}
                  canUseLogical={canUseLogical}
                />
              </ErrorBoundary>
              <AdjustmentBox
                value={invoice?.invoice_adjustment}
                onChange={handleAdjustmentChange}
                computedTotalCents={computedTotalCents}
                adjustedTotalCents={adjustedTotalCents}
                outOfRange={adjustmentOutOfRange}
                formatCurrency={formatCurrency}
              />

              <CollapsedProductsTable
                data={(invoice.invoice_detail || []).map(item => ({
                  ...item,
                  description: typeof item.description === 'string' ? item.description :
                    (item.description?.description || item.description?.name || ''),
                  code: typeof item.code === 'string' ? item.code : item.code?.code || '',
                  prformat: typeof item.prformat === 'string' ? item.prformat :
                    (item.prformat?.name || item.prformat?.prformat || ''),
                  price: typeof item.price === 'number' ? item.price :
                    (item.price?.price || Number(item.price) || 0),
                  finalPrice: typeof item.finalPrice === 'number' ? item.finalPrice :
                    (item.finalPrice?.price || Number(item.finalPrice) || 0),
                }))}
                columns={columnsSummary}
                renderExtras={renderExtras}
                pagination={true}
                rowsPerPage={5}
                summary={summary}
              />
            </Box>
          );
        case 3: // Finalizar ingreso
          return (
            <Box sx={{ marginTop: '15px' }}>
              <SuccessMessage
                onNewInvoice={handleReset}
                success={showSuccessMessage ? "Factura registrada con exito!" : undefined}
                subtitle={"Listo, ahora puedes verlo desde el listado de facturas."}
                alert={arrivedViaSkip
                  ? "Factura no confirmada, Para cancelarla debes confirmarla."
                  : showNoNotesAlert ? "Factura registrada sin notas de entrada." : undefined}
              />

            </Box>
          );
        default:
          return <Typography>Paso no implementado</Typography>;
      }

    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 0.5,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stepper nonLinear activeStep={activeStep} sx={{ width: 'max-content', minWidth: '100%', px: 0.3, '& .MuiStep-root': { flexShrink: 0 }, '& .MuiStepConnector-line': { minHeight: 2 }, }}></Stepper>
        <Stepper nonLinear activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit" disabled={completed[index]}>
                {completed[index] ? 'Completado' : label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      <div>
        {activeStep === steps.length - 1 ? (
          <ErrorBoundary>
            {getStepContent(activeStep)}
          </ErrorBoundary>
        ) : allStepsCompleted() ? (
          <React.Fragment>
            <Typography sx={{ mt: 2, mb: 1 }}>
              Proceso de facturación completado
            </Typography>
          </React.Fragment>
        ) : (
          <>
            <ErrorBoundary>
              {getStepContent(activeStep)}
            </ErrorBoundary>
            <CustomModal
              open={openDocPreview}
              setOpen={setOpenDocPreview}
              handleClose={() => setOpenDocPreview(false)}
              component={DocumentPreview}
              width='60%'
              title='Documento adjunto'
              icon={Image}
              componentProps={{ src: docPreviewSrc || docPreviewSrcResolved, type: docPreviewType, onDelete: clearDocument }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3 }}>
              <RefreshButtonSpinner
                onClick={handleReset}
              />

              <Button
                size="small"
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIosNew />}
                sx={{
                  ml: 2,
                  textTransform: 'none',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  color: 'text.primary',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Atrás
              </Button>

              <Box sx={{ flex: '1 1 auto' }} />

              {isPenultimateStep() && (
                <Tooltip title="Saltar este paso y crear la factura sin confirmar" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    endIcon={<SkipNext />}
                    onClick={handleSkip}
                    sx={{
                      mr: 2,
                      textTransform: 'none',
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      color: 'secondary.main',
                      borderColor: 'secondary.main',
                      '&:hover': {
                        backgroundColor: 'secondary.main',
                        color: '#fff',
                      },
                    }}
                  >
                    Saltar
                  </Button>
                </Tooltip>
              )}

              {isPenultimateStep() && !invoiceHasLinkedNotes && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
                  Sin notas vinculadas; puedes saltar este paso.
                </Typography>
              )}

              {(!isPenultimateStep() || invoiceHasLinkedNotes) && (
                <Button
                  size="small"
                  onClick={handleNext}
                  disabled={!isStepComplete() || loading}
                  endIcon={!loading && <ArrowForwardIos />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    px: 2.5,
                    fontSize: '0.8rem',
                    backgroundColor: isStepComplete() && !loading ? 'primary.main' : 'transparent',
                    color: isStepComplete() && !loading ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: isStepComplete() && !loading ? 'primary.main' : 'divider',
                    cursor: isStepComplete() && !loading ? 'pointer' : 'not-allowed',
                    '&:hover': {
                      backgroundColor: isStepComplete() && !loading ? '#1c2559' : 'transparent',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : isLastStep() ? (
                    'Finalizar'
                  ) : (
                    'Siguiente'
                  )}
                </Button>
              )}
            </Box>

            <CustomSnackBar
              open={snack.open}
              setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
              message={snack.message}
              severity={snack.severity}
            />

          </>
        )}
      </div>
    </Box>
  );
}

export default React.memo(CustomStepperInvoice);

